import React from "react";
import ButtonC from "../../../../button";
import styles from "./index.module.scss";
import { formatCurrency } from "../../../../../../utils/utils";
import { useEligiblePropertiesInfinite } from "../../../../../../hooks/react-query/calling-pipeline/useEligibleProperties";
import { useNavigate } from "react-router-dom";
import CheckoutBtn from "../../../../flipper-vault-card/checkoutButton";
import { useQueryClient } from "@tanstack/react-query";

export default function PropertiesList({ accountId }: { accountId: string }) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useEligiblePropertiesInfinite(accountId);
  const loadMoreRef = React.useRef<HTMLDivElement | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const items = React.useMemo(
    () => data?.pages?.flatMap((p: any) => p?.data || []) || [],
    [data]
  );

  React.useEffect(() => {
    if (!loadMoreRef.current) return;
    const el = loadMoreRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first.isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { root: null, rootMargin: "0px", threshold: 1.0 }
    );
    observer.observe(el);
    return () => observer.unobserve(el);
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  if (isLoading) {
    return (
      <div className={styles.propertiesWrap}>
        <div className={styles.propertiesItem}>
          <div className={styles.loadingState}>
            <span>Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!items || items.length === 0) {
    return (
      <div className={styles.propertiesWrap}>
        <div className={styles.propertiesItem}>
          <div className={styles.noDataState}>
            <span>No Properties Found</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.propertiesWrap}>
      {items.map((item: any) => (
        <div className={styles.propertiesItem} key={item.propertyId}>
          <img
            src={item?.posterImage || "/images/default-property.jpg"}
            alt="Property"
            className={styles.propertyImg}
            onError={(e) => {
              e.currentTarget.src = "/images/default-property.jpg";
            }}
          />
          <div className={styles.content}>
            <div className={styles.addressWrap}>
              <div className={styles.address}>
                <div className={styles.streetName}>
                  {item?.address?.split(",")[0]}
                </div>
                <div className={styles.addressLine}>
                  {item?.address?.split(",")[1]}, {item?.address?.split(",")[2]}
                </div>
              </div>
              <div className={styles.price}>
                {formatCurrency(item.askingPrice, "USD", "en-US", 0, 0)}
              </div>
            </div>
            <div className={styles.btnWrap}>
              <CheckoutBtn
                acctId={accountId}
                propertyId={item.propertyId}
                className={styles.checkoutBtn}
                checkoutBtnWrapClassName={styles.checkoutBtnWrapClassName}
                setHideInfo={() => {}}
                isExpired={false}
                flipperData={{
                  acctSfid: item.acctSfid || "",
                  interestScore: item.interestScore || 0,
                  checkouts: item.checkouts || []
                }}
                type="list"
                onCheckoutSuccess={(pubPropertyId: string) => {
                  queryClient.setQueryData(
                    [
                      "calling-pipeline-eligible-properties-infinite",
                      accountId,
                      new URLSearchParams(window.location.search).toString()
                    ],
                    (oldData: any) => {
                      if (!oldData) return oldData;
                      return {
                        ...oldData,
                        pages: oldData.pages.map((page: any) => ({
                          ...page,
                          data: (page.data || []).filter(
                            (p: any) => p.propertyId !== pubPropertyId
                          )
                        })),
                        pageParams: oldData.pageParams
                      };
                    }
                  );
                  queryClient.invalidateQueries({
                    queryKey: [
                      "calling-pipeline-list",
                      new URLSearchParams(window.location.search).toString()
                    ]
                  });
                }}
              />
              <ButtonC
                btnWrapClassName={styles.DetailsBtnWrap}
                type="button"
                label="View Property"
                className={styles.detailBtn}
                onClick={() => {
                  navigate(`/internal/${item.propertyId}`);
                }}
              />
            </div>
          </div>
        </div>
      ))}
      <div ref={loadMoreRef} />
      {isFetchingNextPage && (
        <div className={styles.propertiesItem}>
          <div className={styles.loadingState}>
            <span>Loading more...</span>
          </div>
        </div>
      )}
    </div>
  );
}
